import type { Ref } from 'vue'

import type { AdvertiserListItem } from './advertiser.types'
import type { RevenueTier } from './common.types'
import { SocialItemEnum } from './socials.types'

export enum FilterCriterionCategoryEnum {
  SocialPlatform = 'filter.platform',
  Client = 'filter.client',
  RevenueTier = 'filter.revenue-tier',
  Analytics = 'filter.analytics',
  Utm = 'filter.utm',
  User = 'filter.user',
}

export const SOCIAL_PLATFORM_FILTER_VALUES = [
  SocialItemEnum.Instagram,
  SocialItemEnum.TikTok,
  SocialItemEnum.Snapchat,
  SocialItemEnum.YouTube,
] as const
export type SocialPlatformFilterValue = (typeof SOCIAL_PLATFORM_FILTER_VALUES)[number]

export enum FilterOperatorEnum {
  GreaterThanOrEqual = 'gte',
  LessThanOrEqual = 'lte',
}

export enum SocialPlatformAttributeEnum {
  Existing = 'existing',
  Connected = 'connected',
  Followers = 'followers',
}

export enum AnalyticsMetricEnum {
  Cost = 'cost',
  Revenue = 'revenue',
  GrossProfit = 'gp',
  Conversion = 'conversion',
  OrderValue = 'orderValue',
  OrderValuePerClick = 'orderValuePerClick',
  EstRevenuePerClick = 'estRevenuePerClick',
}

export type AnalyticsMetricUnit = 'currency' | '%'

export enum UtmParameterKeyEnum {
  Source = 'utm_source',
  Medium = 'utm_medium',
  Campaign = 'utm_campaign',
  Term = 'utm_term',
  Content = 'utm_content',
}

export enum UserFilterFieldEnum {
  RegisteredAt = 'user.registered_at',
  FirstActiveAt = 'user.first_active_at',
  LastActiveAt = 'user.last_active_at',
}

export type FilterCriterionEntityOption = { id: number; label: string }

export type SocialPlatformCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.SocialPlatform
  platform: SocialPlatformFilterValue | null
  attribute: SocialPlatformAttributeEnum
  operator: FilterOperatorEnum | null
  value: number | null
}

export type ClientCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.Client
  client: FilterCriterionEntityOption | null
}

export type RevenueTierCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.RevenueTier
  revenueTier: FilterCriterionEntityOption | null
}

export type AnalyticsCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.Analytics
  advertiser: Pick<AdvertiserListItem, 'id' | 'label' | 'currency'> | null
  metric: AnalyticsMetricEnum | null
  operator: FilterOperatorEnum | null
  value: number | null
  windowDays: number | null
}

export type UtmCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.Utm
  utmKey: UtmParameterKeyEnum | null
  value: string
}

export type UserCriterion = {
  id: string
  category: FilterCriterionCategoryEnum.User
  field: UserFilterFieldEnum | null
  operator: FilterOperatorEnum | null
  value: number | null
}

export type FilterCriterion =
  | SocialPlatformCriterion
  | ClientCriterion
  | RevenueTierCriterion
  | AnalyticsCriterion
  | UtmCriterion
  | UserCriterion

export type FilterCriteriaAsyncFilter = { query: string }

export type FilterCriteriaAsyncResult<T> = {
  data: Ref<T[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

export type LoadClientsForCriteria = (
  filterRef: Ref<FilterCriteriaAsyncFilter>,
) => FilterCriteriaAsyncResult<FilterCriterionEntityOption>

export type LoadAdvertisersForCriteria = (
  filterRef: Ref<FilterCriteriaAsyncFilter>,
) => FilterCriteriaAsyncResult<Pick<AdvertiserListItem, 'id' | 'label' | 'currency'>>

export type LoadRevenueTiersForCriteria = (
  filterRef: Ref<FilterCriteriaAsyncFilter>,
) => FilterCriteriaAsyncResult<Pick<RevenueTier, 'id' | 'name'>>
